import Section from 'components/shared/Section'
import React, { useState } from 'react'
import PageLayout from 'components/layouts/PageLayout'
import { Box, TextField, Typography, MenuItem, Grid, useMediaQuery, useTheme } from '@material-ui/core'
import ButtonAction from 'components/shared/ButtonAction'
import { useStyles } from 'pages/Contact/styles'
import { CONTACT_SUPPORT_MUTATION } from 'constants/mutations'
import { useMutation } from '@apollo/client'

const subjectOptions = [
  { value: 'general', label: 'General Inquiry' },
  { value: 'support', label: 'Support' },
  { value: 'feedback', label: 'Feedback' },
  { value: 'partnership', label: 'Partnership' },
  { value: 'other', label: 'Other' },
]

const Contact: React.FC = () => {
  const classes = useStyles()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [subject, setSubject] = useState('')
  const [message, setMessage] = useState('')
  const [submitMessage, setSubmitMessage] = useState('')

  const [contactSupport, { loading: contactSupportLoading }] = useMutation(CONTACT_SUPPORT_MUTATION)

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setSubmitMessage('')

    try {
      const resp = await contactSupport({
        variables: { firstName, lastName, email, phone, subject, message },
      })

      if (resp.data.contactSupport.message === 'success') {
        setSubmitMessage("Thank you for your message! We'll get back to you soon.")
      } else {
        setSubmitMessage('Sorry, there was an error sending your message. Please try again.')
      }

      // Reset form
      setFirstName('')
      setLastName('')
      setEmail('')
      setPhone('')
      setSubject('')
      setMessage('')
    } catch (error) {
      setSubmitMessage('Sorry, there was an error sending your message. Please try again.')
    }
  }

  return (
    <PageLayout curlyHeader title='Contact'>
      <Section>
        <Box my={4} textAlign='center'>
          If you have a requests, general feedback about the site or just want to say hello, drop us a line below
        </Box>
        <Box p={4} display='flex' justifyContent='center' className={classes.formBox}>
          <Box className={classes.formContent}>
            <form noValidate autoComplete='off' onSubmit={handleSubmit}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Box>
                    <Typography variant='body1' style={{ marginBottom: '10px' }}>
                      First Name
                    </Typography>
                    <TextField
                      InputProps={{ classes: { root: classes.input, notchedOutline: classes.inputNotchedOutline } }}
                      type='text'
                      variant='outlined'
                      value={firstName}
                      onChange={(event) => setFirstName(event.target.value)}
                      required
                      fullWidth
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box>
                    <Typography variant='body1' style={{ marginBottom: '10px' }}>
                      Last Name
                    </Typography>
                    <TextField
                      InputProps={{ classes: { root: classes.input, notchedOutline: classes.inputNotchedOutline } }}
                      type='text'
                      variant='outlined'
                      value={lastName}
                      onChange={(event) => setLastName(event.target.value)}
                      required
                      fullWidth
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box>
                    <Typography variant='body1' style={{ marginBottom: '10px' }}>
                      Your email address
                    </Typography>
                    <TextField
                      InputProps={{ classes: { root: classes.input, notchedOutline: classes.inputNotchedOutline } }}
                      type='email'
                      variant='outlined'
                      value={email}
                      onChange={(event) => setEmail(event.target.value)}
                      required
                      fullWidth
                    />
                  </Box>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Box>
                    <Typography variant='body1' style={{ marginBottom: '10px' }}>
                      Your Phone
                    </Typography>
                    <TextField
                      InputProps={{ classes: { root: classes.input, notchedOutline: classes.inputNotchedOutline } }}
                      type='tel'
                      variant='outlined'
                      value={phone}
                      onChange={(event) => setPhone(event.target.value)}
                      fullWidth
                    />
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box>
                    <Typography variant='body1' style={{ marginBottom: '10px' }}>
                      Subject:
                    </Typography>
                    <TextField
                      InputProps={{ classes: { root: classes.input, notchedOutline: classes.inputNotchedOutline } }}
                      select
                      variant='outlined'
                      value={subject}
                      onChange={(event) => setSubject(event.target.value)}
                      SelectProps={{ displayEmpty: true }}
                      fullWidth
                    >
                      <MenuItem value='' disabled>
                        -- Select --
                      </MenuItem>
                      {subjectOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box>
                    <Typography variant='body1' style={{ marginBottom: '10px' }}>
                      Your Message
                    </Typography>
                    <TextField
                      InputProps={{ classes: { root: classes.textArea, notchedOutline: classes.inputNotchedOutline } }}
                      multiline
                      rows={6}
                      variant='outlined'
                      value={message}
                      onChange={(event) => setMessage(event.target.value)}
                      fullWidth
                    />
                  </Box>
                </Grid>

                <Grid item xs={12} style={{ textAlign: 'right' }}>
                  <ButtonAction
                    text='Send Message'
                    type='submit'
                    variant='contained'
                    color='primary'
                    size='large'
                    fullWidth={isMobile}
                    loading={contactSupportLoading}
                    disabled={!firstName || !lastName || !email}
                  />
                </Grid>
              </Grid>

              {submitMessage && (
                <Box mt={2}>
                  <Typography color={submitMessage.includes('error') ? 'error' : 'primary'} variant='body2'>
                    {submitMessage}
                  </Typography>
                </Box>
              )}
            </form>
          </Box>
        </Box>
      </Section>
    </PageLayout>
  )
}

export default Contact
